import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// File URL with inline auth (img/video tags cannot send headers)
export const fileUrl = (fileId) => {
  if (!fileId) return "";
  const t = localStorage.getItem("token");
  return `${API}/files/${fileId}${t ? `?auth=${t}` : ""}`;
};

// Product #2: fetch a short-lived, file-scoped ticket, then build a URL that
// contains ONLY that ticket (never the account/session token).
export async function p2FileUrl(slug, download = false) {
  if (!slug) return "";
  const { data } = await api.post("/p2/file-ticket", { slug });
  const params = new URLSearchParams({ ticket: data.ticket });
  if (download) params.set("dl", "1");
  return `${API}/p2/file/${slug}?${params.toString()}`;
}

export function apiError(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  if (d?.msg) return d.msg;
  return e?.message || "Something went wrong. Please try again.";
}

// ---- First-party, cookieless, anonymous visitor & session identity ----
// No cookies, no PII. A random visitor id (localStorage) and session id
// (sessionStorage, 30-min idle) let us count unique visitors and sessions
// without names, emails, or IP addresses.
function _rid() {
  try { return crypto.randomUUID(); }
  catch { return Math.random().toString(36).slice(2) + Date.now().toString(36); }
}
function _visitorId() {
  let v = localStorage.getItem("mb_vid");
  if (!v) { v = _rid(); localStorage.setItem("mb_vid", v); }
  return v;
}
function _sessionId() {
  const now = Date.now();
  const last = Number(sessionStorage.getItem("mb_sid_ts") || 0);
  let s = sessionStorage.getItem("mb_sid");
  if (!s || now - last > 30 * 60 * 1000) {
    s = _rid();
    sessionStorage.setItem("mb_sid", s);
    // Capture the entry source ONCE per session so all page views in the
    // session share the same referrer/UTM attribution (internal SPA
    // navigation must not be miscounted as "Direct").
    sessionStorage.setItem("mb_entry", JSON.stringify(_captureEntry()));
  }
  sessionStorage.setItem("mb_sid_ts", String(now));
  return s;
}
function _device() {
  const ua = navigator.userAgent || "";
  if (/iPad|Tablet|PlayBook|Silk/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) return "tablet";
  if (/Mobi|Android|iPhone|iPod|IEMobile|BlackBerry|Opera Mini/i.test(ua)) return "mobile";
  return "desktop";
}
function _captureEntry() {
  let ref_host = "";
  try {
    const r = document.referrer;
    if (r) {
      const h = new URL(r).hostname;
      if (h && h !== window.location.hostname) ref_host = h;
    }
  } catch (_) { /* ignore */ }
  let utm = { utm_source: "", utm_medium: "", utm_campaign: "" };
  try {
    const p = new URLSearchParams(window.location.search);
    utm = {
      utm_source: p.get("utm_source") || "",
      utm_medium: p.get("utm_medium") || "",
      utm_campaign: p.get("utm_campaign") || "",
    };
  } catch (_) { /* ignore */ }
  return { ref_host, ...utm };
}
function _entry() {
  try { return JSON.parse(sessionStorage.getItem("mb_entry") || "{}"); }
  catch { return {}; }
}
function _baseMeta() {
  return { vid: _visitorId(), sid: _sessionId(), device: _device(), ..._entry() };
}

// First-party behavioural tracking (fire-and-forget). Skips admin sessions so
// the owner's own browsing does not contaminate the performance metrics.
export function track(type, ref) {
  try {
    if (localStorage.getItem("role") === "admin") return;
    api.post("/track", { type, ref, meta: _baseMeta() }).catch(() => {});
  } catch (_) { /* no-op */ }
}

// Anonymous page-view tracking, fired on every route change. The sign-in page
// is never counted (it is the one route the owner must pass through before the
// admin guard above can apply).
let _lastPv = { path: null, at: 0 };
export function trackPageView(path) {
  try {
    if (localStorage.getItem("role") === "admin") return;
    if ((path || "").startsWith("/login")) return;
    const now = Date.now();
    if (_lastPv.path === path && now - _lastPv.at < 1500) return; // guard against double effects
    _lastPv = { path, at: now };
    api.post("/track", { type: "page_view", ref: path, meta: _baseMeta() }).catch(() => {});
  } catch (_) { /* no-op */ }
}

// Same as track(), but survives an immediate full-page navigation (Stripe redirect).
export function trackBeacon(type, ref) {
  try {
    if (localStorage.getItem("role") === "admin") return;
    fetch(`${api.defaults.baseURL}/track`, {
      method: "POST", keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type, ref, meta: _baseMeta() }),
    }).catch(() => {});
  } catch (_) { /* no-op */ }
}
